import { STRINGS } from '@/strings/strings';
import type { BookBuilderStep } from '../status-to-step';

/**
 * Schrittleiste — two Ausprägungen per J-23 (Bau-Spez v3 §7).
 *
 * v3 §5 line 203 verbatim (Schrittleiste row of the Bausteine table):
 *   `flex items-center justify-center gap-4 md:gap-7`
 *   `pt-2.5 pb-2 desktop:pt-4 desktop:pb-3.5`
 *   active underline: `h-0.5 w-full bg-dream-gold`
 *   Position and rhythm from `TopBar.tsx:62-64`; deliberate deviation
 *   in the underline (TopBar uses `h-px bg-world-aa`, here
 *   `h-0.5 bg-dream-gold`).
 *
 * v3 §3 line 141 verbatim — Deckkraftstufen:
 *   erledigt (done)   text-dream-white/72
 *   aktiv   (active)  text-dream-gold
 *   offen   (open)    text-dream-white/32
 * "Unterscheidung über Deckkraft und Unterstrich, nie über eine
 *  zweite Farbe."
 *
 * v3 §4 line 186: `text-xs desktop:text-sm` (12 / 14 px).
 *
 * ═══ TWO BARS, NOT ONE ═══════════════════════════════════════════════
 *
 * Bar A — before payment, five marks (order changed at
 * Position-5 Commit 6b.4, Sebastian ruling 2026-09-02):
 *   Personen · Geschichte · Figuren · Vorschau · Bestellung
 *
 * Bar B — after payment, two marks:
 *   Bestellung · Freigabe
 *
 * The `BookBuilderStep` union chooses the bar and the state:
 *
 *   { kind: 'step', value: N }         → Bar A, mark N active (N ∈ 1..4)
 *   { kind: 'post_payment_waiting' }   → Bar B, Bestellung done, Freigabe OPEN
 *   { kind: 'post_payment_approval' }  → Bar B, Bestellung done, Freigabe ACTIVE
 *   { kind: 'unknown' }                → render nothing
 *
 * v3 §7 line 409 verbatim on why the two Bar-B states differ visually:
 * "in diesem Fenster wird das Buch produziert, der Kunde kann nicht
 *  freigeben. Eine aktive Marke würde eine Handlung anzeigen, die es
 *  nicht gibt."
 *
 * ═══ activeMarkOverride — S-19 HOOK, LIVES ON THE COMPONENT ══════════
 *
 * v3 §9 S-19 verbatim: "Auf Leiste A ruht kein Status auf der Marke
 * 'Bestellung'. Bildschirm 5 braucht entweder einen Checkout-Status
 * oder eine Überschreibung der aktiven Marke durch den rendernden
 * Bildschirm. Leiste A ist so zu bauen, dass die Überschreibung
 * später möglich ist, ohne den Mapper wieder aufzumachen."
 *
 * The override lives HERE, on this component, NOT on the mapper's
 * return union. The mapper stays truthful about what statuses
 * actually produce marks (`value: 1 | 2 | 3 | 4`). The screen that
 * has no status — Phase 6's screen 5 (Bestellung) — passes
 * `activeMarkOverride={5}` at render time to force the Bestellung
 * mark active on Bar A.
 *
 * Semantics:
 *   - applies only when `step.kind === 'step'` (Bar A)
 *   - ignored on Bar B (kind `post_payment_waiting` / `_approval`) and
 *     on `unknown` (nothing to override)
 *   - marks 1..(N-1) render done, N active, N+1..5 open — same
 *     projection function as the status-derived path
 */

/** Bar A mark index. 1-based; 5 covers the Phase-6 screen-5 override. */
type BarAMark = 1 | 2 | 3 | 4 | 5;

export interface SchrittleisteProps {
  readonly step: BookBuilderStep;
  /**
   * Optional Bar A override for the active mark. When set and `step.kind`
   * is `'step'`, this value wins over `step.value`. Intended for the
   * Phase-6 screen 5 (Bestellung), which needs to render Bar A with
   * mark 5 active without a status resting there. See S-19 above.
   */
  readonly activeMarkOverride?: BarAMark;
}

type MarkState = 'done' | 'active' | 'open';

const STATE_TEXT_CLASS: Record<MarkState, string> = {
  done: 'text-dream-white/72',
  active: 'text-dream-gold',
  open: 'text-dream-white/32',
};

const BAR_A_MARKS: readonly BarAMark[] = [1, 2, 3, 4, 5];

function barAMarkState(index: BarAMark, activeValue: BarAMark): MarkState {
  if (index < activeValue) return 'done';
  if (index === activeValue) return 'active';
  return 'open';
}

function barAMarkLabel(index: BarAMark): string {
  switch (index) {
    case 1:
      return STRINGS.book.schrittleiste.barA.personen;
    case 2:
      // 6b.4 — position 2 is now Geschichte (was Titelbild).
      return STRINGS.book.schrittleiste.barA.geschichte;
    case 3:
      // 6b.4 — position 3 is now Figuren (was Geschichte). See the
      // status-to-step.ts file header for the label + reorder
      // rationale.
      return STRINGS.book.schrittleiste.barA.figuren;
    case 4:
      return STRINGS.book.schrittleiste.barA.vorschau;
    case 5:
      return STRINGS.book.schrittleiste.barA.bestellung;
  }
}

interface BarMarkProps {
  readonly label: string;
  readonly state: MarkState;
}

function BarMark({ label, state }: BarMarkProps) {
  return (
    <div
      aria-current={state === 'active' ? 'step' : undefined}
      className={`relative flex flex-col items-center justify-center gap-0.5 whitespace-nowrap leading-none text-xs desktop:text-sm ${STATE_TEXT_CLASS[state]}`}
    >
      <span>{label}</span>
      {state === 'active' && <span aria-hidden="true" className="h-0.5 w-full bg-dream-gold" />}
    </div>
  );
}

function BarA({ activeValue }: { readonly activeValue: BarAMark }) {
  return (
    <nav
      aria-label={STRINGS.book.schrittleiste.barA.ariaLabel}
      className="flex items-center justify-center gap-4 md:gap-7 pt-2.5 pb-2 desktop:pt-4 desktop:pb-3.5"
    >
      {BAR_A_MARKS.map((index) => (
        <BarMark
          key={index}
          label={barAMarkLabel(index)}
          state={barAMarkState(index, activeValue)}
        />
      ))}
    </nav>
  );
}

function BarB({ freigabeState }: { readonly freigabeState: 'open' | 'active' }) {
  return (
    <nav
      aria-label={STRINGS.book.schrittleiste.barB.ariaLabel}
      className="flex items-center justify-center gap-4 md:gap-7 pt-2.5 pb-2 desktop:pt-4 desktop:pb-3.5"
    >
      <BarMark label={STRINGS.book.schrittleiste.barB.bestellung} state="done" />
      <BarMark label={STRINGS.book.schrittleiste.barB.freigabe} state={freigabeState} />
    </nav>
  );
}

export function Schrittleiste({ step, activeMarkOverride }: SchrittleisteProps) {
  switch (step.kind) {
    case 'step':
      return <BarA activeValue={activeMarkOverride ?? step.value} />;
    case 'photo_rejected':
      // The customer is being sent back to Schritt 1's photo.
      // Bar A with Personen active — visually the same mark shape as
      // a fresh `draft_configuring` customer, semantically a different
      // screen (rejection screen, not the Konfigurator).
      return <BarA activeValue={1} />;
    case 'post_payment_waiting':
      return <BarB freigabeState="open" />;
    case 'post_payment_approval':
      return <BarB freigabeState="active" />;
    case 'unknown':
      return null;
  }
}
